import { useState, useCallback } from 'react';
import { Loader2, Eye, CheckCircle2, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function GuestNameBackfillPanel() {
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const runPreview = useCallback(async () => {
    setPreviewing(true);
    setPreview(null);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.guestNameBackfillPreview();
      setPreview(data);
      if (data.total_blank === 0) {
        toast.success('No blank guest names found');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  }, []);

  const runApply = useCallback(async () => {
    setApplying(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.guestNameBackfillApply();
      setResult(data);
      toast.success(
        `Backfill complete: ${data.names_populated_count} real names, ${data.fallback_applied_count} fallback labels`
      );
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="h-5 w-5" />
          Guest Name Backfill
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Scans Uplisting reservations with blank guest names. Re-parses from
          stored webhook payloads where available, applies a deterministic
          fallback label (channel + confirmation code suffix) otherwise.
        </p>

        <div className="flex items-end gap-3 flex-wrap">
          <Button onClick={runPreview} disabled={previewing || applying} variant="outline">
            {previewing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Eye className="h-4 w-4 mr-2" />
            )}
            Preview
          </Button>
          <Button
            onClick={runApply}
            disabled={!preview || applying || previewing || preview.total_blank === 0}
          >
            {applying ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            )}
            Apply
          </Button>
        </div>

        {preview && (
          <div className="rounded-lg border border-blue-200 dark:border-blue-800 p-3 bg-blue-50/50 dark:bg-blue-950/20">
            <div className="flex items-center gap-2 mb-3">
              <Eye className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium">Preview</span>
              <Badge variant="secondary" className="text-xs">
                {preview.total_blank} blank
              </Badge>
            </div>

            {preview.channel_breakdown && Object.keys(preview.channel_breakdown).length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs mb-3">
                {Object.entries(preview.channel_breakdown).map(([ch, counts]) => (
                  <div key={ch} className="rounded border p-2 bg-background">
                    <div className="font-medium capitalize">{ch.replace(/_/g, ' ')}</div>
                    <div className="text-muted-foreground">
                      {counts.real_name} real, {counts.fallback} fallback
                    </div>
                  </div>
                ))}
              </div>
            )}

            {preview.rows && preview.rows.length > 0 && (
              <div className="max-h-64 overflow-auto rounded border bg-background">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-2 py-1 text-left">ID</th>
                      <th className="px-2 py-1 text-left">Channel</th>
                      <th className="px-2 py-1 text-left">Property</th>
                      <th className="px-2 py-1 text-left">Check-in</th>
                      <th className="px-2 py-1 text-left">Before</th>
                      <th className="px-2 py-1 text-left">After</th>
                      <th className="px-2 py-1 text-left">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r) => (
                      <tr key={r.uplisting_id} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono">{r.uplisting_id}</td>
                        <td className="px-2 py-1">{r.channel}</td>
                        <td className="px-2 py-1 max-w-[120px] truncate" title={r.property_name}>
                          {r.property_name}
                        </td>
                        <td className="px-2 py-1">{r.check_in ? new Date(r.check_in).toLocaleDateString() : ''}</td>
                        <td className="px-2 py-1 text-muted-foreground italic">
                          {r.before || '(blank)'}
                        </td>
                        <td className="px-2 py-1 font-medium">{r.after}</td>
                        <td className="px-2 py-1">
                          <Badge variant={r.source === 'webhook_payload' ? 'default' : 'secondary'} className="text-[10px]">
                            {r.source === 'webhook_payload' ? 'payload' : 'fallback'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="rounded-lg border border-green-200 dark:border-green-800 p-3 bg-green-50/50 dark:bg-green-950/20">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium">Applied</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              <div>
                <span className="text-muted-foreground">Total blank:</span>{' '}
                <span className="font-medium">{result.total_blank}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Real names populated:</span>{' '}
                <span className="font-medium text-green-600">{result.names_populated_count}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Fallback labels:</span>{' '}
                <span className="font-medium">{result.fallback_applied_count}</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
